import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import "@knowtex-ai/components/style.css";
import App from "./App";
import "@fontsource/nunito-sans";
import * as Sentry from "@sentry/react";
import ResponsiveWrapper from "./components/ResponsiveWrapper";
import { initAll as initAmplitude } from "@amplitude/unified";
import { installSessionExpiryWatcher } from "./lib/sessionExpiry";
import { installIdleTimeout } from "./lib/idleTimeout";
import { installCspReporting } from "./lib/cspReporting";

const isLocalEnvironment = () => {
  return (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname.startsWith("192.168")
  );
};

// Amplitude is altered for local development to disable session replay and autocapture
if (process.env.REACT_APP_AMPLITUDE_API_KEY) {
  initAmplitude(process.env.REACT_APP_AMPLITUDE_API_KEY, {
    analytics: {
      autocapture: !isLocalEnvironment(),
    },
    sessionReplay: {
      sampleRate: isLocalEnvironment() ? 0 : 1,
    },
  });
}

// Sentry is disabled for local development to avoid noise and false-positives.
// Set REACT_APP_TEST_SENTRY=true to enable it locally; debug mode then echoes every event and log to the console.
const sentryTestMode = process.env.REACT_APP_TEST_SENTRY === "true";
const sentryEnabled =
  Boolean(process.env.REACT_APP_SENTRY_DSN) &&
  Boolean(process.env.REACT_APP_SENTRY_ENV) &&
  (!isLocalEnvironment() || sentryTestMode);

if (sentryEnabled) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENV,
    enableLogs: true,
    debug: sentryTestMode,
    integrations: [
      Sentry.replayIntegration({
        networkDetailAllowUrls: [/.*/],
        maskAllInputs: false,
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.browserTracingIntegration(),
    ],
    tracePropagationTargets: [
      "localhost",
      "https://app-knowtex.com",
      /^https:\/\/yourserver\.io\/api/,
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
  });
}

installSessionExpiryWatcher();
installIdleTimeout();
installCspReporting();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ResponsiveWrapper>
      <App />
    </ResponsiveWrapper>
  </React.StrictMode>
);
